import {
  ArrowUp,
  Check,
  Download,
  Info,
  Play,
  RotateCcw,
  Shuffle,
  Timer,
  Volume2,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import {
  downloadUrl,
  exercises as fallbackExercises,
  loadMiniSession,
  miniSessionRequestFromUrl,
  program as fallbackProgram,
  type Exercise,
  type SessionProgram,
} from "./sessionData";

type Phase = "welcome" | "tutorial" | "exercise" | "complete";
type HlsInstance = import("hls.js").default;

const hlsMimeTypes = new Set([
  "application/vnd.apple.mpegurl",
  "application/x-mpegurl",
  "audio/mpegurl",
  "audio/x-mpegurl",
]);

const SWIPE_TRIGGER_DISTANCE = 72;
const SWIPE_MAX_DISTANCE = 148;
const SWIPE_COMMIT_MS = 210;
const WHEEL_TRIGGER_DISTANCE = 70;

const formatTime = (seconds: number) => {
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return `${minutes}:${remainder.toString().padStart(2, "0")}`;
};

const videoStreamUrl = (exercise: Exercise) =>
  exercise.hlsUrl ||
  exercise.progressive?.find(
    (source) =>
      hlsMimeTypes.has(source.type.toLowerCase()) ||
      source.link.toLowerCase().includes(".m3u8"),
  )?.link ||
  null;

const makeVideoAudible = (video: HTMLVideoElement) => {
  video.defaultMuted = false;
  video.muted = false;
  video.volume = 1;
};

export default function App() {
  const [sessionProgram, setSessionProgram] =
    useState<SessionProgram>(fallbackProgram);
  const [sessionExercises, setSessionExercises] =
    useState<Exercise[]>(fallbackExercises);
  const [isLoadingSession, setIsLoadingSession] = useState(true);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>("welcome");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [remaining, setRemaining] = useState(fallbackExercises[0].duration);
  const [isRunning, setIsRunning] = useState(false);
  const [showHowTo, setShowHowTo] = useState(false);
  const [showSwipeHint, setShowSwipeHint] = useState(false);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isSwipeCommitting, setIsSwipeCommitting] = useState(false);
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  const swipeOffsetRef = useRef(0);
  const swipeHapticFired = useRef(false);
  const swipeCommitTimer = useRef<number | null>(null);
  const wheelUnlockTimer = useRef<number | null>(null);
  const wheelLocked = useRef(false);
  const playCurrentVideoWithSound = useRef<(() => void) | null>(null);
  const sessionRequest = useMemo(() => miniSessionRequestFromUrl(), []);

  const currentExercise = sessionExercises[currentIndex];
  const isComplete = phase === "complete";

  const fetchSession = useCallback(async () => {
    setIsLoadingSession(true);
    setSessionError(null);
    try {
      const payload = await loadMiniSession(sessionRequest);
      setSessionProgram(payload.program);
      setSessionExercises(payload.exercises);
      setCurrentIndex(0);
      setRemaining(payload.exercises[0].duration);
    } catch (error) {
      setSessionError(
        error instanceof Error ? error.message : "Unable to load mini session.",
      );
    } finally {
      setIsLoadingSession(false);
    }
  }, [sessionRequest]);

  useEffect(() => {
    void fetchSession();
  }, [fetchSession]);

  const completeSession = useCallback(() => {
    setIsRunning(false);
    setShowHowTo(false);
    setShowSwipeHint(false);
    setPhase("complete");
  }, []);

  const goToExercise = useCallback(
    (index: number) => {
      const nextIndex = Math.min(
        Math.max(index, 0),
        sessionExercises.length - 1,
      );
      setCurrentIndex(nextIndex);
      setRemaining(sessionExercises[nextIndex].duration);
      setShowHowTo(false);
      setShowSwipeHint(false);
    },
    [sessionExercises],
  );

  const goNext = useCallback(() => {
    if (phase !== "exercise") return;
    if (currentIndex >= sessionExercises.length - 1) {
      completeSession();
      return;
    }
    goToExercise(currentIndex + 1);
  }, [completeSession, currentIndex, goToExercise, phase, sessionExercises]);

  const startTutorial = () => {
    if (isLoadingSession || sessionError || sessionExercises.length < 2) return;
    setCurrentIndex(0);
    setRemaining(sessionExercises[0].duration);
    setPhase("tutorial");
  };

  const startExercise = () => {
    playCurrentVideoWithSound.current?.();
    setPhase("exercise");
    setIsRunning(true);
    setShowSwipeHint(false);
  };

  const registerVideoPlayback = useCallback(
    (playWithSound: (() => void) | null) => {
      playCurrentVideoWithSound.current = playWithSound;
    },
    [],
  );

  const replay = () => {
    setCurrentIndex(0);
    setRemaining(sessionExercises[0]?.duration ?? fallbackExercises[0].duration);
    setIsRunning(false);
    setShowHowTo(false);
    setShowSwipeHint(false);
    setPhase("welcome");
  };

  const setSwipeFeedback = useCallback((offset: number) => {
    const clampedOffset = Math.min(Math.max(offset, 0), SWIPE_MAX_DISTANCE);
    swipeOffsetRef.current = clampedOffset;
    setSwipeOffset(clampedOffset);
  }, []);

  const triggerHaptic = useCallback((duration = 8) => {
    const vibrate = window.navigator.vibrate;
    if (typeof vibrate === "function") {
      vibrate.call(window.navigator, [duration]);
    }
  }, []);

  const clearSwipeGesture = useCallback(() => {
    touchStart.current = null;
    swipeHapticFired.current = false;
    setSwipeFeedback(0);
  }, [setSwipeFeedback]);

  const commitSwipeNext = useCallback(() => {
    if (phase !== "exercise" || isSwipeCommitting) return;

    setShowSwipeHint(false);
    setIsSwipeCommitting(true);
    setSwipeFeedback(SWIPE_MAX_DISTANCE);
    triggerHaptic(12);

    if (swipeCommitTimer.current !== null) {
      window.clearTimeout(swipeCommitTimer.current);
    }

    swipeCommitTimer.current = window.setTimeout(() => {
      goNext();
      setIsSwipeCommitting(false);
      clearSwipeGesture();
      swipeCommitTimer.current = null;
    }, SWIPE_COMMIT_MS);
  }, [
    clearSwipeGesture,
    goNext,
    isSwipeCommitting,
    phase,
    setSwipeFeedback,
    triggerHaptic,
  ]);

  useEffect(() => {
    if (phase !== "exercise" || !isRunning) return undefined;
    const timer = window.setInterval(() => {
      setRemaining((value) => Math.max(value - 1, 0));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [isRunning, phase, currentIndex]);

  useEffect(() => {
    if (phase !== "exercise" || remaining !== 0 || isSwipeCommitting) {
      return undefined;
    }
    const timer = window.setTimeout(goNext, 700);
    return () => window.clearTimeout(timer);
  }, [goNext, isSwipeCommitting, phase, remaining]);

  useEffect(() => {
    if (phase !== "exercise" || !currentExercise || remaining <= 0) {
      setShowSwipeHint(false);
      return;
    }

    const hintLeadTime = Math.max(
      5,
      Math.min(12, Math.round(currentExercise.duration * 0.12)),
    );
    setShowSwipeHint(remaining <= hintLeadTime && !isSwipeCommitting);
  }, [currentExercise, isSwipeCommitting, phase, remaining]);

  useEffect(() => {
    return () => {
      if (swipeCommitTimer.current !== null) {
        window.clearTimeout(swipeCommitTimer.current);
      }
      if (wheelUnlockTimer.current !== null) {
        window.clearTimeout(wheelUnlockTimer.current);
      }
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (phase !== "exercise") return;
      if (isSwipeCommitting) return;
      if (event.key === "ArrowUp" || event.key === "ArrowRight") {
        goNext();
      }
      if (event.key === "ArrowLeft") {
        goToExercise(currentIndex - 1);
      }
      if (event.key === " ") {
        event.preventDefault();
        if (!isRunning) playCurrentVideoWithSound.current?.();
        setIsRunning((value) => !value);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [currentIndex, goNext, goToExercise, isRunning, isSwipeCommitting, phase]);

  const onTouchStart = (event: React.TouchEvent) => {
    if (phase !== "exercise" || isSwipeCommitting) return;
    const touch = event.touches[0];
    if (!touch) return;
    touchStart.current = { x: touch.clientX, y: touch.clientY };
    swipeHapticFired.current = false;
    setSwipeFeedback(0);
  };

  const onTouchMove = (event: React.TouchEvent) => {
    if (phase !== "exercise" || isSwipeCommitting) return;
    const start = touchStart.current;
    const touch = event.touches[0];
    if (!start || !touch) return;

    const deltaY = start.y - touch.clientY;
    const deltaX = Math.abs(touch.clientX - start.x);

    if (deltaY <= 0) {
      if (swipeOffsetRef.current > 0) setSwipeFeedback(0);
      return;
    }

    if (deltaY < 8 || deltaY < deltaX * 0.8) return;

    if (event.cancelable) event.preventDefault();
    setSwipeFeedback(deltaY);

    if (deltaY >= SWIPE_TRIGGER_DISTANCE && !swipeHapticFired.current) {
      swipeHapticFired.current = true;
      triggerHaptic();
    }
  };

  const onTouchEnd = (event: React.TouchEvent) => {
    if (phase !== "exercise" || isSwipeCommitting) {
      clearSwipeGesture();
      return;
    }

    if (swipeOffsetRef.current > 0 && event.cancelable) {
      event.preventDefault();
    }

    if (swipeOffsetRef.current >= SWIPE_TRIGGER_DISTANCE) {
      commitSwipeNext();
      return;
    }

    clearSwipeGesture();
  };

  const onTouchCancel = () => {
    clearSwipeGesture();
  };

  const onWheel = (event: React.WheelEvent) => {
    if (phase !== "exercise") return;
    event.preventDefault();

    if (
      event.deltaY < WHEEL_TRIGGER_DISTANCE ||
      wheelLocked.current ||
      isSwipeCommitting
    ) {
      return;
    }

    wheelLocked.current = true;
    commitSwipeNext();

    if (wheelUnlockTimer.current !== null) {
      window.clearTimeout(wheelUnlockTimer.current);
    }
    wheelUnlockTimer.current = window.setTimeout(() => {
      wheelLocked.current = false;
      wheelUnlockTimer.current = null;
    }, 680);
  };

  const screenClass = `session-shell phase-${phase}`;
  const totalDuration = sessionExercises.reduce(
    (total, exercise) => total + exercise.duration,
    0,
  );

  return (
    <main className="app-root">
      <section
        className={screenClass}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchCancel}
        onWheel={onWheel}
      >
        {(phase === "welcome" || phase === "complete") && (
          <BrandBar isComplete={isComplete} />
        )}

        {phase === "welcome" && (
          <WelcomeScreen
            displayProgram={sessionProgram}
            totalDuration={totalDuration}
            isLoading={isLoadingSession}
            error={sessionError}
            onRetry={fetchSession}
            onStart={startTutorial}
          />
        )}

        {(phase === "tutorial" || phase === "exercise") && currentExercise && (
          <ExerciseSurface
            exercise={currentExercise}
            currentIndex={currentIndex}
            remaining={remaining}
            isRunning={isRunning}
            showHowTo={showHowTo}
            swipeOffset={swipeOffset}
            isSwipeCommitting={isSwipeCommitting}
            onRegisterVideoPlayback={registerVideoPlayback}
            onToggleHowTo={() => {
              setShowHowTo((value) => !value);
            }}
            onTogglePlay={() => {
              if (!isRunning) playCurrentVideoWithSound.current?.();
              setIsRunning((value) => !value);
            }}
          />
        )}

        {phase === "tutorial" && <TutorialOverlay onStart={startExercise} />}

        {phase === "exercise" && showSwipeHint && !showHowTo && (
          <SwipeHint />
        )}

        {phase === "complete" && (
          <CompletionScreen program={sessionProgram} onReplay={replay} />
        )}
      </section>

      <aside className="desktop-rail" aria-hidden="true">
        <p className="rail-kicker">Public Mini Session</p>
        <h2>{sessionProgram.name || fallbackProgram.name}</h2>
        <p>
          A no-login, two-move preview that streams the first exercises from the
          public onboarding program and hands off to the Prehab app.
        </p>
        <div className="rail-list">
          {sessionExercises.map((exercise, index) => (
            <span key={exercise.id}>
              {index + 1}. {exercise.name}
            </span>
          ))}
        </div>
      </aside>
    </main>
  );
}

function BrandBar({ isComplete }: { isComplete: boolean }) {
  return (
    <header className="brand-bar">
      <img src="/assets/prehab-logo-text.svg" alt="Prehab" />
      {!isComplete && (
        <a className="subtle-link" href={downloadUrl}>
          Open app
        </a>
      )}
    </header>
  );
}

function WelcomeScreen({
  displayProgram,
  totalDuration,
  isLoading,
  error,
  onRetry,
  onStart,
}: {
  displayProgram: SessionProgram;
  totalDuration: number;
  isLoading: boolean;
  error: string | null;
  onRetry: () => void;
  onStart: () => void;
}) {
  const minutes = Math.max(1, Math.round(totalDuration / 60));
  const programImageUrl = displayProgram.imageUrl ?? null;
  const [programImageState, setProgramImageState] = useState<
    "idle" | "loaded" | "failed"
  >(programImageUrl ? "idle" : "failed");

  useEffect(() => {
    setProgramImageState(programImageUrl ? "idle" : "failed");
  }, [programImageUrl]);

  const showProgramImage = programImageUrl && programImageState !== "failed";
  const showProgramPlaceholder = !programImageUrl || programImageState !== "loaded";

  return (
    <section className="welcome-screen">
      <div className="program-art-wrap">
        {showProgramPlaceholder ? (
          <div className="program-art-placeholder" aria-hidden="true" />
        ) : null}
        {showProgramImage ? (
          <img
            src={programImageUrl}
            alt=""
            className="program-art"
            onLoad={() => setProgramImageState("loaded")}
            onError={() => setProgramImageState("failed")}
          />
        ) : null}
      </div>

      <div className="welcome-copy">
        <p className="eyebrow">No login needed</p>
        <h1>Let's try a quick session</h1>
        <p className="supporting-copy">
          Two starter moves from {displayProgram.sessionName ?? "the first day"}{" "}
          of the {displayProgram.name}. Follow along, swipe up when you are
          ready, and keep going in the app.
        </p>
      </div>

      <div className="feature-row" aria-label="Session details">
        <span>
          <Timer size={17} aria-hidden="true" />
          About {minutes} min
        </span>
        <span>
          <ArrowUp size={17} aria-hidden="true" />
          Swipe up
        </span>
      </div>

      {error ? (
        <div className="load-error" role="alert">
          <span>{error}</span>
          <button type="button" onClick={onRetry}>
            Try again
          </button>
        </div>
      ) : (
        <button
          className="primary-action"
          onClick={onStart}
          disabled={isLoading}
        >
          <Play size={20} fill="currentColor" aria-hidden="true" />
          {isLoading ? "Loading Session" : "Start Mini Session"}
        </button>
      )}
    </section>
  );
}

function ExerciseSurface({
  exercise,
  currentIndex,
  remaining,
  isRunning,
  showHowTo,
  swipeOffset,
  isSwipeCommitting,
  onRegisterVideoPlayback,
  onToggleHowTo,
  onTogglePlay,
}: {
  exercise: Exercise;
  currentIndex: number;
  remaining: number;
  isRunning: boolean;
  showHowTo: boolean;
  swipeOffset: number;
  isSwipeCommitting: boolean;
  onRegisterVideoPlayback: (playWithSound: (() => void) | null) => void;
  onToggleHowTo: () => void;
  onTogglePlay: () => void;
}) {
  const remainingProgress = useMemo(
    () => remaining / exercise.duration,
    [exercise.duration, remaining],
  );
  const sets = exercise.sets ?? 1;
  const timeLabel = exercise.prescribedTime
    ? `${exercise.prescribedTime}s`
    : formatTime(exercise.duration);
  const exerciseClass = [
    "exercise-screen",
    swipeOffset > 0 ? "is-swiping" : "",
    isSwipeCommitting ? "is-swipe-committing" : "",
  ]
    .filter(Boolean)
    .join(" ");
  const exerciseStyle = {
    "--swipe-y": `${swipeOffset * -1}px`,
  } as CSSProperties;

  return (
    <section className={exerciseClass} style={exerciseStyle}>
      <div className="slide-underlay" aria-hidden="true">
        <ArrowUp size={28} />
      </div>
      <div className={`video-stage accent-${exercise.accent}`}>
        <SessionVideo
          exercise={exercise}
          isRunning={isRunning}
          activeKey={`${exercise.id}-${currentIndex}`}
          onRegisterPlayback={onRegisterVideoPlayback}
        />
        <div className="video-scrim" />

        <div className="bottom-controls">
          <div className="timer-box">
            <button
              className="icon-button"
              type="button"
              onClick={onTogglePlay}
              title={isRunning ? "Pause timer" : "Play timer"}
              aria-label={isRunning ? "Pause timer" : "Play timer"}
            >
              {isRunning ? (
                <span className="pause-glyph" aria-hidden="true" />
              ) : (
                <Play size={21} fill="currentColor" aria-hidden="true" />
              )}
            </button>
            <div className="timer-copy">
              <strong>{formatTime(remaining)}</strong>
              <div className="linear-meter" aria-hidden="true">
                <i
                  style={{
                    transform: `scaleX(${Math.max(remainingProgress, 0.03)})`,
                  }}
                />
              </div>
            </div>
          </div>

          <div className="info-chips" aria-label="Exercise details">
            <span>
              Sets <strong>1/{sets}</strong>
            </span>
            <span>
              Time <strong>{timeLabel}</strong>
            </span>
          </div>

          <div className="player-actions">
            <a className="glass-action" href={downloadUrl}>
              <Shuffle size={17} aria-hidden="true" />
              Modify
            </a>
            <button
              className="glass-action"
              type="button"
              onClick={onToggleHowTo}
              aria-expanded={showHowTo}
            >
              <Info size={18} aria-hidden="true" />
              How to
            </button>
          </div>

          <div className={`how-to-body ${showHowTo ? "open" : ""}`}>
            <ol>
              {exercise.instructions.length > 0 ? (
                exercise.instructions.map((instruction) => (
                  <li key={instruction}>{instruction}</li>
                ))
              ) : (
                <li>{exercise.prescription}</li>
              )}
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
}

function SessionVideo({
  exercise,
  isRunning,
  activeKey,
  onRegisterPlayback,
}: {
  exercise: Exercise;
  isRunning: boolean;
  activeKey: string;
  onRegisterPlayback: (playWithSound: (() => void) | null) => void;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamUrl = useMemo(() => videoStreamUrl(exercise), [exercise]);
  const [isSourceReady, setIsSourceReady] = useState(false);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [needsUserPlayback, setNeedsUserPlayback] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !streamUrl) return undefined;

    let cancelled = false;
    setIsSourceReady(false);
    setIsVideoReady(false);
    setVideoError(false);
    setNeedsUserPlayback(false);
    video.pause();
    video.removeAttribute("src");
    makeVideoAudible(video);
    video.loop = true;
    (video as HTMLVideoElement & { referrerPolicy?: string }).referrerPolicy =
      "no-referrer";
    video.load();

    const markReady = () => {
      if (cancelled) return;
      setIsSourceReady(true);
      setIsVideoReady(true);
      setVideoError(false);
    };

    let hls: HlsInstance | null = null;
    void import("hls.js").then(({ default: Hls }) => {
      if (cancelled) return;
      if (Hls.isSupported()) {
        hls = new Hls({
          enableWorker: true,
          lowLatencyMode: false,
        });
        hls.attachMedia(video);
        hls.on(Hls.Events.MEDIA_ATTACHED, () => {
          hls?.loadSource(streamUrl);
        });
        hls.on(Hls.Events.MANIFEST_PARSED, markReady);
        hls.on(Hls.Events.ERROR, (_event, data) => {
          if (!data.fatal) return;
          if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
            hls?.startLoad();
            return;
          }
          if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
            hls?.recoverMediaError();
            return;
          }
          setVideoError(true);
        });
        return;
      }

      if (video.canPlayType("application/vnd.apple.mpegurl")) {
        video.src = streamUrl;
        video.load();
        setIsSourceReady(true);
        return;
      }

      setVideoError(true);
    });

    const onError = () => {
      if (!cancelled) setVideoError(true);
    };
    video.addEventListener("loadedmetadata", markReady);
    video.addEventListener("loadeddata", markReady);
    video.addEventListener("canplay", markReady);
    video.addEventListener("playing", markReady);
    video.addEventListener("error", onError);

    return () => {
      cancelled = true;
      video.removeEventListener("loadedmetadata", markReady);
      video.removeEventListener("loadeddata", markReady);
      video.removeEventListener("canplay", markReady);
      video.removeEventListener("playing", markReady);
      video.removeEventListener("error", onError);
      hls?.destroy();
    };
  }, [activeKey, streamUrl]);

  const playAudibly = useCallback(() => {
    const video = videoRef.current;
    if (!video || !streamUrl || videoError) return;

    makeVideoAudible(video);
    setNeedsUserPlayback(false);

    void video
      .play()
      .then(() => {
        if (videoRef.current !== video) return;
        setIsVideoReady(true);
        setNeedsUserPlayback(false);
      })
      .catch((error: unknown) => {
        if (videoRef.current !== video) return;

        const errorName = error instanceof Error ? error.name : "";
        if (errorName === "AbortError") return;
        if (errorName === "NotAllowedError") {
          if (video.readyState > 0) setIsVideoReady(true);
          setNeedsUserPlayback(true);
          return;
        }

        if (video.readyState > 0) setVideoError(true);
      });
  }, [streamUrl, videoError]);

  useLayoutEffect(() => {
    onRegisterPlayback(playAudibly);
    return () => onRegisterPlayback(null);
  }, [onRegisterPlayback, playAudibly]);

  useLayoutEffect(() => {
    const video = videoRef.current;
    if (!video || !streamUrl || !isSourceReady || videoError) return;
    if (isRunning) {
      playAudibly();
      return;
    }

    video.pause();
    setNeedsUserPlayback(false);
  }, [isRunning, isSourceReady, playAudibly, streamUrl, videoError]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !isRunning) return undefined;

    const onEnded = () => {
      if (!video.loop) setNeedsUserPlayback(false);
    };
    const onPause = () => {
      if (!isRunning || video.ended) return;
      setNeedsUserPlayback(true);
    };

    video.addEventListener("ended", onEnded);
    video.addEventListener("pause", onPause);
    return () => {
      video.removeEventListener("ended", onEnded);
      video.removeEventListener("pause", onPause);
    };
  }, [isRunning, activeKey]);

  useEffect(() => {
    if (!isRunning) return;
    const video = videoRef.current;
    if (video && video.paused && isSourceReady && !videoError) {
      setNeedsUserPlayback(true);
    }
  }, [isRunning, isSourceReady, videoError]);

  const resumeWithSound = () => {
    if (isRunning) {
      playAudibly();
    } else {
      const video = videoRef.current;
      if (!video) return;
      makeVideoAudible(video);
      video.pause();
    }
  };

  if (!streamUrl) {
    return (
      <div className="video-fallback">
        {exercise.posterUrl ? <img src={exercise.posterUrl} alt="" /> : null}
        <strong>Video unavailable</strong>
      </div>
    );
  }

  return (
    <>
      <video
        ref={videoRef}
        poster={exercise.posterUrl ?? undefined}
        playsInline
        preload="auto"
      />
      {!isVideoReady && !videoError ? (
        <div className="video-state">Loading video</div>
      ) : null}
      {needsUserPlayback && isRunning && isSourceReady && !videoError ? (
        <button
          className="video-state video-sound-button"
          type="button"
          onClick={resumeWithSound}
        >
          <Volume2 size={16} aria-hidden="true" />
          Tap for sound
        </button>
      ) : null}
      {videoError ? (
        <div className="video-state error">Tap play or refresh to reload video</div>
      ) : null}
    </>
  );
}

function TutorialOverlay({ onStart }: { onStart: () => void }) {
  return (
    <section className="tutorial-overlay">
      <div className="tutorial-panel">
        <div className="arrow-stack" aria-hidden="true">
          <ArrowUp size={42} />
          <ArrowUp size={34} />
          <span />
        </div>
        <h2>Swipe up to move on</h2>
        <p>Follow the timer or advance whenever you are ready.</p>
        <div className="tutorial-pills">
          <span>
            <Play size={17} aria-hidden="true" />
            Follow along
          </span>
          <span>
            <Info size={17} aria-hidden="true" />
            How to
          </span>
        </div>
        <button className="primary-action" type="button" onClick={onStart}>
          Start
        </button>
      </div>
    </section>
  );
}

function SwipeHint() {
  return (
    <div className="swipe-hint" aria-hidden="true">
      <div className="arrow-stack compact">
        <ArrowUp size={38} />
        <ArrowUp size={30} />
        <span />
      </div>
      <strong>Swipe up</strong>
    </div>
  );
}

function CompletionScreen({
  program,
  onReplay,
}: {
  program: SessionProgram;
  onReplay: () => void;
}) {
  return (
    <section className="completion-screen">
      <div className="success-mark">
        <Check size={38} aria-hidden="true" />
      </div>
      <p className="eyebrow">Mini session complete</p>
      <h1>Great work.</h1>
      <p className="supporting-copy">
        Download Prehab to save your personalized program and continue the full{" "}
        {program.name}.
      </p>
      <a className="primary-action" href={downloadUrl}>
        <Download size={20} aria-hidden="true" />
        Download the App
      </a>
      <button className="secondary-action" type="button" onClick={onReplay}>
        <RotateCcw size={18} aria-hidden="true" />
        Replay Mini Session
      </button>
      <p className="medical-note">
        Stop if symptoms increase. This preview is educational and is not a
        substitute for medical care.
      </p>
    </section>
  );
}
